/**
 * Utilitários de integração com a BrasilAPI.
 * Distingue 4 tipos de erro: not_found | network | server | unknown
 */

export interface CnpjData {
  razao_social: string
  nome_fantasia?: string
  cnpj: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  municipio?: string
  uf?: string
  cep?: string
  email?: string
  telefone?: string
}

export interface CepData {
  cep: string
  state: string
  city: string
  neighborhood: string
  street: string
}

export class BrasilAPIError extends Error {
  constructor(
    message: string,
    public readonly kind: 'not_found' | 'network' | 'server' | 'unknown',
    public readonly status?: number,
  ) {
    super(message)
    this.name = 'BrasilAPIError'
  }
}

const TIMEOUT_MS = 8000

async function fetchComTimeout(url: string): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const resp = await fetch(url, { signal: controller.signal })
    clearTimeout(timeoutId)
    return resp
  } catch (err) {
    clearTimeout(timeoutId)
    if (err instanceof Error && err.name === 'AbortError') {
      throw new BrasilAPIError(
        'Sem conexão com a BrasilAPI — preencha manualmente',
        'network',
      )
    }
    if (err instanceof TypeError) {
      throw new BrasilAPIError(
        'Sem conexão com a BrasilAPI — preencha manualmente',
        'network',
      )
    }
    throw new BrasilAPIError('Erro desconhecido na consulta', 'unknown')
  }
}

function tratarStatusHttp(resp: Response, entidade: string): void {
  if (resp.status === 404) {
    // BrasilAPI retorna 404 para CNPJs reais mas que não estão na base dela
    // (limitação da BrasilAPI). Diferenciar essa situação de "CNPJ inválido".
    throw new BrasilAPIError(
      `${entidade} não encontrado na BrasilAPI. Pode existir na Receita mas estar fora da base BrasilAPI — preencha manualmente.`,
      'not_found',
      404,
    )
  }
  if (resp.status === 400) {
    // BrasilAPI retorna 400 quando o CNPJ é estruturalmente inválido
    // (dígitos verificadores incorretos). Mensagem explícita.
    throw new BrasilAPIError(
      `${entidade} inválido — verifique os dígitos.`,
      'not_found',
      400,
    )
  }
  if (resp.status === 429) {
    throw new BrasilAPIError(
      'BrasilAPI atingiu limite de requisições — tente em alguns segundos.',
      'server',
      429,
    )
  }
  if (resp.status >= 500) {
    throw new BrasilAPIError(
      'Serviço temporariamente indisponível — preencha manualmente',
      'server',
      resp.status,
    )
  }
  if (!resp.ok) {
    throw new BrasilAPIError(
      `Erro ao consultar ${entidade} (${resp.status})`,
      'unknown',
      resp.status,
    )
  }
}

interface MinhaReceitaResponse {
  cnpj?: string
  razao_social?: string
  nome_fantasia?: string
  logradouro?: string
  descricao_tipo_de_logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  municipio?: string
  uf?: string
  cep?: string
  email?: string | null
  ddd_telefone_1?: string
}

function adaptarMinhaReceita(d: MinhaReceitaResponse): CnpjData {
  const tipo = d.descricao_tipo_de_logradouro?.trim()
  const logradouro = [tipo, d.logradouro].filter(Boolean).join(' ').trim()
  return {
    cnpj: d.cnpj ?? '',
    razao_social: d.razao_social ?? '',
    nome_fantasia: d.nome_fantasia ?? undefined,
    logradouro: logradouro || undefined,
    numero: d.numero ?? undefined,
    complemento: d.complemento ?? undefined,
    bairro: d.bairro ?? undefined,
    municipio: d.municipio ?? undefined,
    uf: d.uf ?? undefined,
    cep: d.cep ?? undefined,
    email: d.email ?? undefined,
    telefone: d.ddd_telefone_1 ?? undefined,
  }
}

async function buscarCnpjMinhaReceita(digitos: string): Promise<CnpjData> {
  const resp = await fetchComTimeout(`https://minhareceita.org/${digitos}`)
  if (resp.status === 404) {
    throw new BrasilAPIError(
      'CNPJ não encontrado. Verifique os dígitos ou preencha manualmente.',
      'not_found',
      404,
    )
  }
  if (!resp.ok) {
    throw new BrasilAPIError(
      `Erro ao consultar CNPJ na MinhaReceita (${resp.status}) — preencha manualmente`,
      'server',
      resp.status,
    )
  }
  const d = (await resp.json()) as MinhaReceitaResponse
  return adaptarMinhaReceita(d)
}

export async function buscarCnpj(cnpj: string): Promise<CnpjData> {
  const digitos = cnpj.replace(/\D/g, '')
  if (digitos.length !== 14) {
    throw new BrasilAPIError('CNPJ deve ter 14 dígitos', 'unknown')
  }

  // 1ª tentativa: BrasilAPI (rápida, mas base limitada)
  let primaryError: BrasilAPIError | null = null
  try {
    const resp = await fetchComTimeout(`https://brasilapi.com.br/api/cnpj/v1/${digitos}`)
    if (resp.status === 400) {
      throw new BrasilAPIError('CNPJ inválido — verifique os dígitos.', 'not_found', 400)
    }
    if (resp.ok) {
      const d = (await resp.json()) as CnpjData
      // Normaliza CNPJ retornado (pode vir formatado XX.XXX.XXX/XXXX-XX)
      d.cnpj = (d.cnpj ?? digitos).replace(/\D/g, '')
      return d
    }
    primaryError = new BrasilAPIError(
      `BrasilAPI retornou ${resp.status}`,
      resp.status === 404
        ? 'not_found'
        : resp.status === 429 || resp.status >= 500
          ? 'server'
          : 'unknown',
      resp.status,
    )
  } catch (err) {
    if (err instanceof BrasilAPIError && err.kind === 'not_found' && err.status === 400) {
      throw err  // CNPJ estruturalmente inválido — não tenta fallback
    }
    if (err instanceof BrasilAPIError) {
      primaryError = err
    } else {
      primaryError = new BrasilAPIError('BrasilAPI indisponível', 'network')
    }
  }

  // 2ª tentativa: MinhaReceita (fallback — base completa da Receita Federal)
  try {
    const dados = await buscarCnpjMinhaReceita(digitos)
    dados.cnpj = (dados.cnpj || digitos).replace(/\D/g, '')
    return dados
  } catch (err) {
    if (err instanceof BrasilAPIError && err.kind === 'not_found') {
      throw err
    }
    // Fallback falhou — retorna o erro da BrasilAPI primária (mais informativo)
    throw primaryError ?? err
  }
}

export async function buscarCep(cep: string): Promise<CepData> {
  const digitos = cep.replace(/\D/g, '')
  if (digitos.length !== 8) {
    throw new BrasilAPIError('CEP deve ter 8 dígitos', 'unknown')
  }

  const resp = await fetchComTimeout(
    `https://brasilapi.com.br/api/cep/v1/${digitos}`,
  )
  tratarStatusHttp(resp, 'CEP')
  return resp.json() as Promise<CepData>
}
